import "./Quote.css";
import { useState } from "react";

function Quote() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        location: "",
        type: "",
        capacity: "",
        timeline: "",
        budget: "",
        message: "",
    });

    const [status, setStatus] = useState({
        loading: false,
        success: false,
        error: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: "" });

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("name", formData.name);
            formDataToSend.append("email", formData.email);
            formDataToSend.append("phone", formData.phone);
            formDataToSend.append("location", formData.location);
            formDataToSend.append("type", formData.type);
            formDataToSend.append("capacity", formData.capacity);
            formDataToSend.append("timeline", formData.timeline);
            formDataToSend.append("budget", formData.budget);
            formDataToSend.append("message", formData.message);

            const response = await fetch("/api/quote.php", {
                method: "POST",
                body: formDataToSend,
            });

            const data = await response.json();

            if (data.success) {
                setStatus({ loading: false, success: true, error: "" });
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    location: "",
                    type: "",
                    capacity: "",
                    timeline: "",
                    budget: "",
                    message: "",
                });
                setTimeout(() => {
                    setStatus({ loading: false, success: false, error: "" });
                }, 5000);
            } else {
                setStatus({
                    loading: false,
                    success: false,
                    error: data.error || "Failed to send quote request",
                });
            }
        } catch (error) {
            setStatus({
                loading: false,
                success: false,
                error: "Network error. Please try again.",
            });
        }
    };

    return (
        <section className="quote-section">
            <div className="quote-container">
                <div className="quote-header">
                    <p className="quote-kicker">Request a Quote</p>
                    <h1 className="quote-title">Get a tailored solar proposal</h1>
                    <p className="quote-subtitle">
                        Tell us about your project and we will craft a customized solution with
                        transparent pricing, timelines, and expected savings.
                    </p>
                </div>

                <div className="quote-highlights">
                    <div className="highlight-card">
                        <span className="highlight-icon">⚡</span>
                        <h4>48h Response</h4>
                        <p>Initial estimate within two working days</p>
                    </div>
                    <div className="highlight-card">
                        <span className="highlight-icon">🏠</span>
                        <h4>Custom Design</h4>
                        <p>Solutions sized to your roof & profile</p>
                    </div>
                    <div className="highlight-card">
                        <span className="highlight-icon">💰</span>
                        <h4>Transparent Pricing</h4>
                        <p>Clear breakdown of all costs</p>
                    </div>
                </div>

                <div className="quote-content">
                    <div className="quote-form-container">
                        <div className="form-heading">
                            <h3>Tell us about your project</h3>
                            <p>We'll design a solution tailored to your needs.</p>
                        </div>

                        {/* Success Message */}
                        {status.success && (
                            <div className="alert alert-success">
                                ✓ Quote request sent successfully! We'll contact you within 48 hours.
                            </div>
                        )}

                        {/* Error Message */}
                        {status.error && (
                            <div className="alert alert-error">
                                ✗ {status.error}
                            </div>
                        )}

                        <form className="quote-form" onSubmit={handleSubmit}>
                            <div className="form-section">
                                <div className="section-label">Contact Information</div>
                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label htmlFor="name">Full Name *</label>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            placeholder="John Doe"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email">Email Address *</label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone Number</label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            placeholder="+91 9876543210"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="location">City / Location</label>
                                        <input
                                            id="location"
                                            name="location"
                                            type="text"
                                            placeholder="Palanpur"
                                            value={formData.location}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <div className="section-label">Project Details</div>
                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label htmlFor="type">Project Type *</label>
                                        <select
                                            id="type"
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select project type</option>
                                            <option value="residential">Residential</option>
                                            <option value="commercial">Commercial</option>
                                            <option value="industrial">Industrial</option>
                                            <option value="storage">Energy Storage</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="capacity">Approx. Capacity Needed *</label>
                                        <input
                                            id="capacity"
                                            name="capacity"
                                            type="text"
                                            placeholder="5-10 kW"
                                            value={formData.capacity}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <div className="section-label">Timeline & Details</div>
                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label htmlFor="timeline">Preferred Timeline *</label>
                                        <select
                                            id="timeline"
                                            name="timeline"
                                            value={formData.timeline}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select timeline</option>
                                            <option value="1-2">1-2 months</option>
                                            <option value="3-4">3-4 months</option>
                                            <option value="5-6">5-6 months</option>
                                            <option value="flexible">Flexible</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="budget">Budget Range</label>
                                        <select
                                            id="budget"
                                            name="budget"
                                            value={formData.budget}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select range</option>
                                            <option value="<5">Below ₹5 Lakhs</option>
                                            <option value="5-10">₹5-10 Lakhs</option>
                                            <option value="10-20">₹10-20 Lakhs</option>
                                            <option value="20+">₹20+ Lakhs</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <div className="form-group full-width">
                                    <label htmlFor="message">Additional Information</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows="4"
                                        placeholder="Tell us about your roof, current electricity usage, or any specific concerns..."
                                        value={formData.message}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="quote-button"
                                disabled={status.loading}
                            >
                                {status.loading ? "Sending..." : "Get Your Free Quote"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Quote;